import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  Signal,
  WritableSignal
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { Task } from '@app/shared/types/models/task/task.model';

@Component({
  selector: 'proffeo-select-parent-task-tree-node',
  templateUrl: './select-parent-task-tree-node.component.html',
  imports: [MatIconModule, MatRadioModule, MatFormFieldModule]
})
export class SelectParentTaskTreeNodeComponent {
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);

  protected readonly projectId: Signal<string> = computed(() => this.task().project().id);
  protected readonly children: Signal<Task[]> = computed(() => {
    const list = this.tasksDataService.getTasksByParent(this.projectId(), this.task().id)() ?? [];
    return list.filter((child): child is Task => child != null);
  });
  protected readonly isExpanded = computed(() => this.expandedIds().has(this.task().id));
  protected readonly showChevron = computed(() => (this.task().subtasksCount ?? 0) > 0 || this.children().length > 0);
  protected readonly isForbidden = computed(() => this.forbiddenIds().has(this.task().id));
  protected readonly isSelected = computed(() => this.selectedParentId()() === this.task().id);

  public readonly expandedIds: ModelSignal<Set<string>> = model.required<Set<string>>();
  public readonly task: InputSignal<Task> = input.required<Task>();
  public readonly depth: InputSignal<number> = input(0);
  public readonly forbiddenIds: InputSignal<Set<string>> = input.required<Set<string>>();
  public readonly selectedParentId: InputSignal<WritableSignal<string | null>> =
    input.required<WritableSignal<string | null>>();

  protected toggleExpand(event: Event): void {
    event.stopPropagation();
    if (!this.showChevron()) return;
    const id = this.task().id;
    this.expandedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  protected selectTask(): void {
    if (this.isForbidden()) return;
    this.selectedParentId().set(this.task().id);
  }

  protected getTaskIdentifier(): string {
    return `${this.task().name} (${this.task().taskType().code}‑${this.task().taskNumber?.replaceAll('-', '‑')})`;
  }
}
